import { useEffect, useState } from "react";
import React from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR, formatIDRShort } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, LineChart, Line } from "recharts";
import { Wallet, BookOpen, Users, Check, TrendingUp as TrendingUpIcon, History, Printer, TrendingDown, Globe, ChevronDown, ChevronRight } from "lucide-react";
import { flagFor } from "../lib/countries";

const nowYear = new Date().getFullYear();
const years = [nowYear - 2, nowYear - 1, nowYear, nowYear + 1];

export default function Reports() {
  const [year, setYear] = useState(nowYear);
  const [monthly, setMonthly] = useState(null);
  const [yearly, setYearly] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState(null);  // month number when expanded
  const [monthExpenses, setMonthExpenses] = useState({});    // {ymKey: array}
  const [loadingExpense, setLoadingExpense] = useState(null);

  const toggleMonth = async (month) => {
    if (expandedMonth === month) {
      setExpandedMonth(null);
      return;
    }
    setExpandedMonth(month);
    const ymKey = `${year}-${String(month).padStart(2, "0")}`;
    if (!monthExpenses[ymKey]) {
      setLoadingExpense(month);
      try {
        const r = await api.get(`/expenses?year=${year}&month=${month}`);
        setMonthExpenses((prev) => ({ ...prev, [ymKey]: r.data || [] }));
      } finally {
        setLoadingExpense(null);
      }
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, y, e] = await Promise.all([
        api.get(`/reports/monthly?year=${year}`),
        api.get(`/reports/yearly`),
        api.get(`/expenses/summary?year=${year}`),
      ]);
      setMonthly(m.data);
      setYearly(y.data);
      setExpenseSummary(e.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  return (
    <>
      <PageHeader
        title="Laporan Keuangan"
        subtitle="Analisis pendapatan bulanan dan tahunan dari seluruh booking Confirmed & Paid."
        actions={
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="report-year-select" className="w-[130px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              asChild
              variant="outline"
              data-testid="btn-print-monthly"
              className="border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] gap-2 bg-white"
            >
              <Link to={`/reports/print/monthly/${year}`} target="_blank">
                <Printer className="h-4 w-4" /> Cetak Bulanan
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="btn-print-yearly"
              className="border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] gap-2 bg-white"
            >
              <Link to={`/reports/print/yearly/${year}`} target="_blank">
                <Printer className="h-4 w-4" /> Cetak Tahunan
              </Link>
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="monthly" className="w-full">
        <TabsList className="bg-[#EBE6DE]/50 p-1 rounded-xl">
          <TabsTrigger value="monthly" data-testid="tab-monthly" className="data-[state=active]:bg-white data-[state=active]:text-[#172E21] rounded-lg px-5">Bulanan</TabsTrigger>
          <TabsTrigger value="yearly" data-testid="tab-yearly" className="data-[state=active]:bg-white data-[state=active]:text-[#172E21] rounded-lg px-5">Tahunan</TabsTrigger>
          <TabsTrigger value="country" data-testid="tab-country" className="data-[state=active]:bg-white data-[state=active]:text-[#172E21] rounded-lg px-5">Asal Tamu</TabsTrigger>
          <TabsTrigger value="range" data-testid="tab-range" className="data-[state=active]:bg-white data-[state=active]:text-[#172E21] rounded-lg px-5">Range Kustom</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast" className="data-[state=active]:bg-white data-[state=active]:text-[#172E21] rounded-lg px-5">Forecast</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly" className="mt-6 flex flex-col gap-6">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            <SummaryCard icon={<Wallet className="h-5 w-5" />} label={`Pendapatan ${year}`} value={formatIDRShort(monthly?.total_revenue || 0)} subtext={formatIDR(monthly?.total_revenue || 0)} accent="#D16B4D" testid="summary-year-revenue" />
            <SummaryCard icon={<TrendingDown className="h-5 w-5" />} label={`Pengeluaran ${year}`} value={formatIDRShort(expenseSummary?.total || 0)} subtext={formatIDR(expenseSummary?.total || 0)} accent="#D14D4D" testid="summary-year-expenses" />
            <SummaryCard icon={<TrendingUpIcon className="h-5 w-5" />} label={`Profit Bersih ${year}`} value={formatIDRShort((monthly?.total_revenue || 0) - (expenseSummary?.total || 0))} subtext={formatIDR((monthly?.total_revenue || 0) - (expenseSummary?.total || 0))} accent={(monthly?.total_revenue || 0) - (expenseSummary?.total || 0) >= 0 ? "#2C7A51" : "#D14D4D"} testid="summary-year-profit" />
            <SummaryCard icon={<BookOpen className="h-5 w-5" />} label={`Booking ${year}`} value={monthly?.total_bookings ?? 0} subtext={`${monthly?.total_guests ?? 0} tamu`} accent="#082618" testid="summary-year-bookings" />
          </div>

          {/* Chart */}
          <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
            <div className="mb-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Trend Bulanan</div>
              <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Pendapatan per Bulan — {year}</h3>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly?.months || []} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
                  <XAxis dataKey="month_name" tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}jt` : v)} tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #EBE6DE" }} />
                  <Bar dataKey="revenue" fill="#D16B4D" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FDFBF7] hover:bg-[#FDFBF7]">
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider w-8"></TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Bulan</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Booking</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Tamu</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Pendapatan</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Pengeluaran</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Profit</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Sisa Tagihan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={8} className="text-center py-10 text-[#586D61]">Memuat…</TableCell></TableRow>}
                {monthly?.months?.map((m) => {
                  const ymKey = `${year}-${String(m.month).padStart(2, "0")}`;
                  const monthExpense = expenseSummary?.by_month?.[ymKey] || 0;
                  const profit = (m.revenue || 0) - monthExpense;
                  const isExpanded = expandedMonth === m.month;
                  const hasExpenses = monthExpense > 0;
                  return (
                    <React.Fragment key={m.month}>
                      <TableRow
                        data-testid={`report-month-row-${m.month}`}
                        className={`${hasExpenses ? "cursor-pointer" : ""} hover:bg-[#FDFBF7] ${isExpanded ? "bg-[#FDFBF7]" : ""}`}
                        onClick={() => hasExpenses && toggleMonth(m.month)}
                      >
                        <TableCell className="text-[#586D61]">
                          {hasExpenses ? (
                            isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                          ) : null}
                        </TableCell>
                        <TableCell className="font-medium text-[#172E21]">{m.month_name}</TableCell>
                        <TableCell className="text-center font-mono text-sm">{m.bookings}</TableCell>
                        <TableCell className="text-center font-mono text-sm">{m.guests}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-[#172E21] font-medium">{formatIDR(m.revenue)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-[#D14D4D]">{monthExpense > 0 ? `-${formatIDR(monthExpense)}` : "—"}</TableCell>
                        <TableCell className={`text-right font-mono text-sm font-bold ${profit >= 0 ? "text-[#2C7A51]" : "text-[#D14D4D]"}`}>{formatIDR(profit)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-[#D99338]">{formatIDR(m.outstanding)}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow data-testid={`expense-detail-row-${m.month}`} className="bg-[#FDFBF7]/60 hover:bg-[#FDFBF7]/60">
                          <TableCell colSpan={8} className="p-0">
                            <ExpenseDetailRow
                              loading={loadingExpense === m.month}
                              expenses={monthExpenses[ymKey] || []}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="yearly" className="mt-6 flex flex-col gap-6">
          <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
            <div className="mb-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Trend Tahunan</div>
              <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Pendapatan per Tahun</h3>
            </div>
            <div className="h-80">
              {yearly?.years?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yearly.years} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}jt` : v)} tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #EBE6DE" }} />
                    <Line type="monotone" dataKey="revenue" stroke="#D16B4D" strokeWidth={3} dot={{ r: 5, fill: "#D16B4D" }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-[#586D61]">Belum ada data booking.</div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FDFBF7] hover:bg-[#FDFBF7]">
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Tahun</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Booking</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Tamu</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Pendapatan</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">DP Terbayar</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Sisa Tagihan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!yearly?.years || yearly.years.length === 0) && (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#586D61]">Belum ada data tahunan.</TableCell></TableRow>
                )}
                {yearly?.years?.slice().sort((a, b) => (b.year || 0) - (a.year || 0)).map((y) => (
                  <TableRow key={y.year} data-testid={`report-year-row-${y.year}`} className="hover:bg-[#FDFBF7]">
                    <TableCell className="font-medium text-[#172E21] font-mono">{y.year}</TableCell>
                    <TableCell className="text-center font-mono text-sm">{y.bookings}</TableCell>
                    <TableCell className="text-center font-mono text-sm">{y.guests}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#172E21] font-medium">{formatIDR(y.revenue)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#2C7A51]">{formatIDR(y.deposits)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#D99338]">{formatIDR(y.outstanding)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="country" className="mt-6 flex flex-col gap-6">
          <CountryReportTab year={year} />
        </TabsContent>

        <TabsContent value="range">
          <RangeTab year={year} />
        </TabsContent>

        <TabsContent value="forecast">
          <ForecastTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

function ExpenseDetailRow({ loading, expenses }) {
  if (loading) {
    return <div className="px-6 py-4 text-sm text-[#586D61]">Memuat detail pengeluaran…</div>;
  }
  if (!expenses || expenses.length === 0) {
    return <div className="px-6 py-4 text-sm text-[#586D61] italic">Tidak ada detail pengeluaran untuk bulan ini.</div>;
  }
  // Group by category
  const byCat = {};
  expenses.forEach((e) => {
    const cat = e.category || "Lainnya";
    if (!byCat[cat]) byCat[cat] = { total: 0, items: [] };
    byCat[cat].total += Number(e.amount || 0);
    byCat[cat].items.push(e);
  });
  const cats = Object.entries(byCat).sort((a, b) => b[1].total - a[1].total);
  const grandTotal = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="px-6 py-4 border-l-4 border-[#D14D4D]/40">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">
          Detail Pengeluaran ({expenses.length} transaksi)
        </div>
        <div className="text-sm font-mono font-bold text-[#D14D4D]">
          Total: -{formatIDR(grandTotal)}
        </div>
      </div>

      {/* Breakdown by Category */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {cats.map(([cat, data]) => (
          <div key={cat} className="flex items-center justify-between bg-white border border-[#EBE6DE] rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-block h-2 w-2 rounded-full bg-[#D14D4D] flex-shrink-0" />
              <span className="text-xs font-medium text-[#172E21] truncate">{cat}</span>
              <span className="text-[10px] text-[#586D61]">({data.items.length})</span>
            </div>
            <span className="text-xs font-mono font-bold text-[#D14D4D]">{formatIDR(data.total)}</span>
          </div>
        ))}
      </div>

      {/* Item list */}
      <div className="bg-white border border-[#EBE6DE] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#FDFBF7]">
            <tr>
              <th className="text-left px-3 py-2 font-bold text-[#586D61] uppercase tracking-wider text-[10px]">Tanggal</th>
              <th className="text-left px-3 py-2 font-bold text-[#586D61] uppercase tracking-wider text-[10px]">Kategori</th>
              <th className="text-left px-3 py-2 font-bold text-[#586D61] uppercase tracking-wider text-[10px]">Deskripsi</th>
              <th className="text-right px-3 py-2 font-bold text-[#586D61] uppercase tracking-wider text-[10px]">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-line-${e.id}`} className="border-t border-[#EBE6DE] hover:bg-[#FDFBF7]/60">
                <td className="px-3 py-2 font-mono text-[#586D61]">{e.date}</td>
                <td className="px-3 py-2">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[#D14D4D]/10 text-[#D14D4D] text-[10px] font-medium">{e.category || "Lainnya"}</span>
                </td>
                <td className="px-3 py-2 text-[#172E21]">{e.description || <span className="italic text-[#586D61]/60">—</span>}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#D14D4D]">-{formatIDR(e.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CountryReportTab({ year }) {
  const [scope, setScope] = useState("year"); // year | all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = scope === "year" ? `/reports/by-country?year=${year}` : `/reports/by-country`;
    api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [year, scope]);

  if (loading) return <div className="text-center py-12 text-[#586D61]">Memuat…</div>;
  const countries = data?.countries || [];

  return (
    <>
      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#2C7A51]/15 text-[#2C7A51] flex items-center justify-center">
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Asal Tamu — Revenue Breakdown</div>
            <div className="font-heading text-xl font-medium text-[#172E21] mt-0.5">
              {scope === "year" ? `Tahun ${year}` : "Semua Periode"}
            </div>
          </div>
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="country-scope-select" className="w-[200px] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="year">Tahun {year}</SelectItem>
            <SelectItem value="all">Semua Periode (All Time)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiSmall label="Total Revenue" value={formatIDR(data?.total_revenue || 0)} icon={Wallet} accent="#D16B4D" />
        <KpiSmall label="Total Booking" value={data?.total_bookings || 0} icon={BookOpen} accent="#2C7A51" />
        <KpiSmall label="Total Tamu" value={data?.total_guests || 0} icon={Users} accent="#586D61" />
      </div>

      {countries.length === 0 ? (
        <div className="bg-white border border-[#EBE6DE] rounded-2xl p-12 text-center">
          <Globe className="h-12 w-12 mx-auto text-[#586D61]/40 mb-3" />
          <div className="font-heading text-xl text-[#172E21]">Belum ada data asal tamu</div>
          <p className="text-sm text-[#586D61] mt-2">Edit booking di menu Bookings dan isi field "Asal Negara" untuk melihat breakdown di sini.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="country-report-table">
              <TableHeader>
                <TableRow className="bg-[#FDFBF7]">
                  <TableHead className="font-bold text-[#172E21]">Negara</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Booking</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Tamu</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Malam</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Revenue</TableHead>
                  <TableHead className="font-bold text-[#172E21]">% Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {countries.map((c) => (
                  <TableRow key={c.country} data-testid={`country-row-${c.country}`} className="hover:bg-[#FDFBF7]">
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium text-[#172E21]">
                        <span className="text-xl leading-none">{flagFor(c.country)}</span>
                        <span>{c.country}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{c.bookings}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{c.guests}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{c.nights}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-[#D16B4D]">{formatIDR(c.revenue)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[140px]">
                        <div className="flex-1 h-2 rounded-full bg-[#EBE6DE] overflow-hidden">
                          <div
                            className="h-full bg-[#D16B4D] rounded-full transition-all"
                            style={{ width: `${Math.min(100, c.pct).toFixed(1)}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono text-[#586D61] w-12 text-right">{c.pct.toFixed(1)}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </>
  );
}

function KpiSmall({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#EBE6DE] rounded-2xl p-5 flex items-center gap-4">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}18`, color: accent }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#586D61]">{label}</div>
        <div className="text-xl font-mono font-medium text-[#172E21] mt-0.5">{value}</div>
      </div>
    </div>
  );
}


function ForecastTab() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  useEffect(() => {
    api.get(`/forecast?months_ahead=${months}`).then((r) => setData(r.data));
  }, [months]);
  if (!data) return <div className="text-[#586D61] mt-6">Memuat…</div>;

  const totalProjected = data.forecast.reduce((s, m) => s + m.projected_revenue, 0);
  const totalConfirmed = data.forecast.reduce((s, m) => s + m.confirmed_revenue, 0);

  return (
    <div className="flex flex-col gap-6 mt-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="kpi-card bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-3" data-testid="forecast-confirmed">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#2C7A51]/15 text-[#2C7A51]">
            <Check className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#586D61]">Confirmed Revenue (Depan)</div>
            <div className="text-3xl font-mono font-medium tracking-tight text-[#172E21] mt-2">{formatIDRShort(totalConfirmed)}</div>
            <div className="text-xs text-[#586D61] mt-1 font-mono">{formatIDR(totalConfirmed)}</div>
          </div>
        </div>
        <div className="kpi-card bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-3" data-testid="forecast-projected">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#D16B4D]/15 text-[#D16B4D]">
            <TrendingUpIcon className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#586D61]">Projected Total ({months} bulan)</div>
            <div className="text-3xl font-mono font-medium tracking-tight text-[#172E21] mt-2">{formatIDRShort(totalProjected)}</div>
            <div className="text-xs text-[#586D61] mt-1 font-mono">{formatIDR(totalProjected)}</div>
          </div>
        </div>
        <div className="kpi-card bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-3" data-testid="forecast-avg">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#082618]/10 text-[#082618]">
            <History className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#586D61]">Rata-rata Bulanan Historis</div>
            <div className="text-3xl font-mono font-medium tracking-tight text-[#172E21] mt-2">{formatIDRShort(data.historical_avg_monthly)}</div>
            <div className="text-xs text-[#586D61] mt-1">Dari {data.months_sampled} bulan terakhir</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Proyeksi Pendapatan</div>
            <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">{months} Bulan ke Depan</h3>
          </div>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="w-[140px] bg-white" data-testid="forecast-months-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 Bulan</SelectItem>
              <SelectItem value="6">6 Bulan</SelectItem>
              <SelectItem value="12">12 Bulan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.forecast} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
              <XAxis dataKey="month_name" tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}jt` : v)} tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #EBE6DE" }} />
              <Bar dataKey="confirmed_revenue" fill="#2C7A51" name="Confirmed" radius={[8, 8, 0, 0]} />
              <Bar dataKey="projected_revenue" fill="#D16B4D" name="Projected" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="text-xs text-[#586D61] mt-4 leading-relaxed">
          <strong className="text-[#172E21]">Hijau (Confirmed):</strong> pendapatan dari booking Confirmed/Paid yang sudah ada untuk bulan tersebut.&nbsp;
          <strong className="text-[#D16B4D]">Oranye (Projected):</strong> proyeksi berdasarkan data terkuat dari confirmed revenue atau rata-rata bulanan historis.
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, subtext, accent, testid }) {
  return (
    <div data-testid={testid} className="kpi-card bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-3">
      <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}18`, color: accent }}>
        {icon}
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#586D61]">{label}</div>
        <div className="text-3xl font-mono font-medium tracking-tight text-[#172E21] mt-2">{value}</div>
        {subtext && <div className="text-xs text-[#586D61] mt-1 font-mono">{subtext}</div>}
      </div>
    </div>
  );
}

function RangeTab({ year }) {
  // Default: this year-to-date
  const todayIso = new Date().toISOString().slice(0, 10);
  const defaultStart = `${year}-01-01`;
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const applyPreset = (s, e) => { setStart(s); setEnd(e); };
  const presets = [
    { label: `Q1 ${year}`, start: `${year}-01-01`, end: `${year}-03-31`, key: "q1" },
    { label: `Q2 ${year}`, start: `${year}-04-01`, end: `${year}-06-30`, key: "q2" },
    { label: `Q3 ${year}`, start: `${year}-07-01`, end: `${year}-09-30`, key: "q3" },
    { label: `Q4 ${year}`, start: `${year}-10-01`, end: `${year}-12-31`, key: "q4" },
    { label: `YTD ${year}`, start: `${year}-01-01`, end: todayIso, key: "ytd" },
  ];

  const fetchRange = async () => {
    if (!start || !end) { setError("Pilih tanggal mulai & akhir"); return; }
    if (new Date(end) < new Date(start)) { setError("Tanggal akhir harus >= tanggal mulai"); return; }
    setError(null);
    setLoading(true);
    try {
      const r = await api.get(`/reports/range?start=${start}&end=${end}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal memuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRange(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="flex flex-col gap-6 mt-6">
      {/* Filter card */}
      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Filter Periode</div>
          <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Range Kustom</h3>
          <p className="text-sm text-[#586D61] mt-1">Pilih preset cepat atau atur tanggal manual.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-5" data-testid="range-presets">
          {presets.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant="outline"
              data-testid={`preset-${p.key}`}
              onClick={() => applyPreset(p.start, p.end)}
              className={`bg-white border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] ${start === p.start && end === p.end ? "border-[#D16B4D] text-[#D16B4D] bg-[#D16B4D]/8" : ""}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-sm font-medium">Dari Tanggal</Label>
            <Input
              type="date"
              data-testid="input-range-start"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1.5 border-[#EBE6DE] font-mono"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Sampai Tanggal</Label>
            <Input
              type="date"
              data-testid="input-range-end"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1.5 border-[#EBE6DE] font-mono"
            />
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="btn-apply-range"
              onClick={fetchRange}
              disabled={loading}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl flex-1"
            >
              {loading ? "Memuat…" : "Terapkan"}
            </Button>
            <Button
              asChild
              variant="outline"
              disabled={!start || !end}
              data-testid="btn-print-range"
              className="border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] bg-white gap-1.5"
            >
              <Link to={`/reports/print/range/${start}/${end}`} target="_blank">
                <Printer className="h-4 w-4" /> Cetak
              </Link>
            </Button>
          </div>
        </div>
        {error && <div className="text-xs text-[#D14D4D] mt-3">{error}</div>}
      </div>

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <SummaryCard icon={<Wallet className="h-5 w-5" />} label="Total Pendapatan" value={formatIDRShort(data.total_revenue)} subtext={formatIDR(data.total_revenue)} accent="#D16B4D" testid="range-summary-revenue" />
            <SummaryCard icon={<BookOpen className="h-5 w-5" />} label="Total Booking" value={data.total_bookings} accent="#2C7A51" testid="range-summary-bookings" />
            <SummaryCard icon={<Users className="h-5 w-5" />} label="Total Tamu" value={data.total_guests} accent="#082618" testid="range-summary-guests" />
          </div>

          {/* Chart */}
          <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
            <div className="mb-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Trend Periode</div>
              <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Pendapatan Per Bulan</h3>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.months} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
                  <XAxis dataKey="month_name" tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}jt` : v)} tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #EBE6DE" }} />
                  <Bar dataKey="revenue" fill="#D16B4D" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FDFBF7] hover:bg-[#FDFBF7]">
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Bulan</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Booking</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Tamu</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Pendapatan</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">DP Terbayar</TableHead>
                  <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Sisa Tagihan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.months.map((m, i) => (
                  <TableRow key={i} data-testid={`range-row-${i}`} className="hover:bg-[#FDFBF7]">
                    <TableCell className="font-medium text-[#172E21]">{m.month_name}</TableCell>
                    <TableCell className="text-center font-mono text-sm">{m.bookings}</TableCell>
                    <TableCell className="text-center font-mono text-sm">{m.guests}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#172E21] font-medium">{formatIDR(m.revenue)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#2C7A51]">{formatIDR(m.deposits)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#D99338]">{formatIDR(m.outstanding)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

